
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Rnd } from 'react-rnd';
import { Play, Pause, GripHorizontal, Settings2, Maximize2, Minimize2, Clock, Calendar, Radio, RotateCcw } from 'lucide-react';
import { BacktestingStatus } from '../types/backtesting';
import { useStores } from '../store';
import './BacktestingControl.css';

interface BacktestingControlProps {
  onStatusChange?: (status: BacktestingStatus) => void;
}

const BacktestingControl: React.FC<BacktestingControlProps> = observer(({ onStatusChange }) => {
  const stores = useStores();
  const { replayController } = stores;
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('09:30');
  
  const [minDate, setMinDate] = useState<string>('');
  const [maxDate, setMaxDate] = useState<string>('');

  // Track the size/position to restore when maximizing
  const [size, setSize] = useState({ width: 360, height: 450 });
  
  // Track current symbol to detect changes
  const [previousSymbol, setPreviousSymbol] = useState<string | undefined>(stores?.state?.symbol);

  useEffect(() => {
    const today = new Date();
    const fiveMonthsAgo = new Date();
    fiveMonthsAgo.setMonth(today.getMonth() - 5);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const formatTime = (date: Date) => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    
    setMinDate(formatDate(fiveMonthsAgo));
    setMaxDate(formatDate(today));
    setSelectedDate(formatDate(today));
    setSelectedTime(formatTime(today));
  }, []);

  // Reset backtesting when symbol changes
  useEffect(() => {
    const currentSymbol = stores?.state?.symbol;
    
    if (previousSymbol && currentSymbol && previousSymbol !== currentSymbol) {
      console.log('[BacktestingControl] Symbol changed from', previousSymbol, 'to', currentSymbol);
      
      // Stop replay if running
      if (replayController.mode !== 'live') {
        console.log('[BacktestingControl] Stopping replay due to symbol change');
        replayController.stop();
      }
      
      // Reset to default time (current time)
      const today = new Date();
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const formatTime = (date: Date) => {
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      };
      
      setSelectedDate(formatDate(today));
      setSelectedTime(formatTime(today));
      setIsMinimized(false);
      
      onStatusChange?.(BacktestingStatus.PAUSED);
    }
    
    setPreviousSymbol(currentSymbol);
  }, [stores?.state?.symbol, previousSymbol, replayController, onStatusChange]);

  // Derive status from replay controller state
  const status = replayController.isPlaying 
    ? BacktestingStatus.RUNNING 
    : (replayController.mode === 'replay' || replayController.mode === 'paused')
    ? BacktestingStatus.PAUSED
    : BacktestingStatus.PAUSED;

  const handleTogglePlay = async () => {
    try {
      if (status === BacktestingStatus.RUNNING) {
        // Pause
        replayController.pause();
        onStatusChange?.(BacktestingStatus.PAUSED);
      } else if (replayController.mode === 'live') {
        // If in live mode, start a new backtest from selected date/time
        console.log('[BacktestingControl] Starting backtest from live mode');
        await handleStartNew();
      } else {
        // Resume playback from current position
        console.log('[BacktestingControl] Resuming backtest');
        await replayController.play();
        onStatusChange?.(BacktestingStatus.RUNNING);
        
        // Auto minimize when resuming
        setIsMinimized(true);
      }
    } catch (error) {
      console.error('[BacktestingControl] Error toggling play:', error);
    }
  };

  const handleStartNew = async () => {
    try {
      // Start fresh replay from selected date/time
      // User inputs local time, create Date object which interprets as local, then get UTC epoch
      const dateTimeString = `${selectedDate}T${selectedTime}:00`;
      const localDate = new Date(dateTimeString);
      const targetEpoch = localDate.getTime();
      
      console.log('[BacktestingControl] Selected date/time (LOCAL):', selectedDate, selectedTime);
      console.log('[BacktestingControl] Local date object:', localDate.toString());
      console.log('[BacktestingControl] Target epoch (UTC):', targetEpoch, '=', new Date(targetEpoch).toUTCString());
      console.log('[BacktestingControl] Starting NEW backtest from:', new Date(targetEpoch));
      
      // First, stop any current replay to clear state
      if (replayController.mode !== 'live') {
        console.log('[BacktestingControl] Stopping current replay before starting new one');
        replayController.stop();
        
        // Wait a bit for cleanup to complete
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Now start fresh from the new target epoch
      await replayController.play(targetEpoch, 1);
      
      onStatusChange?.(BacktestingStatus.RUNNING);
      
      // Auto minimize when starting
      setIsMinimized(true);
    } catch (error) {
      console.error('[BacktestingControl] Error starting new backtest:', error);
    }
  };

  const handleGoLive = () => {
    console.log('[BacktestingControl] Going back to live mode');
    replayController.jumpToLive();
  };

  const getStatusColor = () => {
    switch (status) {
      case BacktestingStatus.RUNNING: return 'bg-sky-50 text-sky-700 border-sky-200';
      case BacktestingStatus.PAUSED: return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const renderMinimized = () => (
    <div className="w-full h-full bg-white border border-slate-200 rounded-xl shadow-xl flex items-center px-3 gap-2 ring-1 ring-black/5">
      <div className="drag-handle cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600 shrink-0">
        <GripHorizontal size={18} />
      </div>
      
      <button
        onClick={() => setIsMinimized(false)}
        className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all shrink-0"
        title="Expand Control Panel"
      >
        <Maximize2 size={18} />
      </button>

      <div className="h-8 w-px bg-slate-100 shrink-0" />
      
      <div className="flex items-center gap-2 shrink-0">
        <div className={`w-2 h-2 rounded-full ${status === BacktestingStatus.RUNNING ? 'bg-sky-500 animate-pulse' : 'bg-amber-500'}`} />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700 whitespace-nowrap">{status}</span>
      </div>

      <div className="flex-1 min-w-[8px]" />

      <div className="flex items-center gap-2 shrink-0">
        {replayController.mode !== 'live' && (
          <button
            onClick={handleStartNew}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
            title="Start New Backtest"
          >
            <RotateCcw size={18} />
          </button>
        )}

        <button
          onClick={handleTogglePlay}
          className={`p-2 rounded-lg transition-all ${
            status === BacktestingStatus.PAUSED 
              ? 'text-indigo-600 hover:bg-indigo-50' 
              : 'text-amber-600 hover:bg-amber-50'
          }`}
          title={status === BacktestingStatus.PAUSED ? "Start" : "Pause"}
        >
          {status === BacktestingStatus.PAUSED ? <Play size={20} fill="currentColor" /> : <Pause size={20} fill="currentColor" />}
        </button>

        {replayController.mode !== 'live' && (
          <button
            onClick={handleGoLive}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
            title="Go Live"
          >
            <Radio size={20} />
          </button>
        )}
      </div>
    </div>
  );

  const renderFull = () => (
    <div className="w-full h-full bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden ring-1 ring-black/5">
      {/* Header / Drag Handle */}
      <div className="drag-handle flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-grab active:cursor-grabbing shrink-0">
        <div className="flex items-center gap-2">
          <Settings2 size={16} className="text-indigo-600" />
          <span className="text-sm font-bold tracking-wide text-slate-800 uppercase">Backtest Control</span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsMinimized(true)}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 rounded-md transition-all"
            title="Minimize"
          >
            <Minimize2 size={16} />
          </button>
          <GripHorizontal size={20} className="text-slate-400" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1 gap-8 overflow-y-auto">
        
        {/* Status Display Area */}
        <section>
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3 block">Current Status</label>
          <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${getStatusColor()}`}>
            <div className="flex flex-col">
              <span className="text-xs font-semibold opacity-70">EXECUTION MODE</span>
              <span className="text-xl font-black uppercase tracking-tight">{status}</span>
            </div>
            <div className={`p-3 rounded-full bg-black/5`}>
              {status === BacktestingStatus.RUNNING ? (
                <Play size={20} className="fill-current animate-pulse text-sky-600" />
              ) : (
                <Pause size={20} className="fill-current text-amber-600" />
              )}
            </div>
          </div>
        </section>

        {/* Configuration */}
        <section className="space-y-5">
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block">Simulation Start Point</label>
          
          <div className="space-y-4">
            {/* Date Input */}
            <div className="relative group">
              <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
                <div className="pl-3 flex-shrink-0 text-slate-400 group-focus-within:text-indigo-600 transition-colors">
                  <Calendar size={14} />
                </div>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="flex-1 bg-transparent py-3 pl-2 pr-4 text-sm font-medium text-slate-900 outline-none"
                />
              </div>
              <span className="absolute -top-2 left-2 bg-white px-1 text-[10px] text-slate-500 group-focus-within:text-indigo-600">Start Date</span>
            </div>

            {/* Time Input */}
            <div className="relative group">
              <div className="flex items-center bg-slate-50 border border-slate-200 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
                <div className="pl-3 flex-shrink-0 text-slate-400 group-focus-within:text-indigo-600 transition-colors">
                  <Clock size={14} />
                </div>
                <input
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="flex-1 bg-transparent py-3 pl-2 pr-4 text-sm font-medium text-slate-900 outline-none"
                />
              </div>
              <span className="absolute -top-2 left-2 bg-white px-1 text-[10px] text-slate-500 group-focus-within:text-indigo-600">Start Time</span>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 leading-relaxed italic">
            {status === BacktestingStatus.RUNNING 
              ? "* Inputs are locked during active simulation." 
              : `* Historical data window: ${minDate} to ${maxDate}.`}
          </p>
        </section>

        {/* Action Buttons */}
        <section className="mt-auto space-y-3">
          {replayController.mode !== 'live' && (
            <button
              onClick={handleStartNew}
              className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all transform active:scale-[0.98] bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
              title="Start a new backtest from the selected date and time"
            >
              <RotateCcw size={18} />
              START NEW FROM DATE
            </button>
          )}
          
          <button
            onClick={handleTogglePlay}
            className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all transform active:scale-[0.98] shadow-lg shadow-black/5 ${
              status === BacktestingStatus.PAUSED 
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20' 
                : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-500/20'
            }`}
          >
            {status === BacktestingStatus.PAUSED ? (
              <>
                <Play size={20} fill="currentColor" />
                {replayController.mode === 'live' ? 'START BACKTEST' : 'RESUME'}
              </>
            ) : (
              <>
                <Pause size={20} fill="currentColor" />
                PAUSE
              </>
            )}
          </button>
          
          {replayController.mode !== 'live' && (
            <button
              onClick={handleGoLive}
              className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all transform active:scale-[0.98] bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              title="Exit backtesting and return to live mode"
            >
              <Radio size={18} />
              GO LIVE
            </button>
          )}
        </section>
      </div>

      {/* Resizer Indicators (Visual Only) */}
      <div className="absolute bottom-0 right-0 w-4 h-4 bg-slate-100 rounded-tl-xl cursor-nwse-resize flex items-end justify-end p-0.5">
        <div className="w-1.5 h-1.5 border-r border-b border-slate-300" />
      </div>
    </div>
  );

  return (
    <Rnd
      size={isMinimized ? { width: 280, height: 60 } : size}
      minWidth={isMinimized ? 280 : 320}
      minHeight={isMinimized ? 60 : 400}
      maxWidth={isMinimized ? 280 : 600}
      maxHeight={isMinimized ? 60 : 800}
      enableResizing={!isMinimized}
      onResizeStop={(_e, _direction, ref, _delta, _position) => {
        setSize({
          width: parseInt(ref.style.width),
          height: parseInt(ref.style.height),
        });
      }}
      bounds="parent"
      dragHandleClassName="drag-handle"
      className="z-50"
      /* Fix: react-rnd's default prop requires x, y, width, and height */
      default={{
        x: 100,
        y: 100,
        width: 360,
        height: 450,
      }}
    >
      {isMinimized ? renderMinimized() : renderFull()}
    </Rnd>
  );
});

export default BacktestingControl;
